import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { AutomationsHub } from "@/features/automations";
import { automationsListQueryOptions } from "@/features/automations/hooks/useAutomations";
import { WorkspaceSearchSchema } from "@/features/workspace-router";

/**
 * Extends the shared workspace search schema (``sub``/``focus``/… still
 * deep-link, per this route's contract) with an OPTIONAL ``automation`` id.
 * When present, the hub renders the dedicated ``AutomationDetailSection``
 * takeover instead of the list — URL-addressable (back/forward + refresh
 * work, no drawer overlay). ``automation`` is optional, so every existing
 * ``<Link to="/workspace/automations">`` / ``navigate`` call stays valid
 * without a search.
 */
const AutomationsSearchSchema = WorkspaceSearchSchema.extend({
  automation: z.string().optional(),
});

/**
 * ``/workspace/automations`` — workspace surface for scheduled / reactive
 * AI workers.
 *
 * The route shares the workspace search schema (``sub``/``focus``)
 * so deep-links still work. It does NOT participate in
 * ``WORKSPACE_TAB_DEFS`` because the ``WorkspaceTab`` zod union (in
 * ``@ando/core/domain``) is the cross-package source of truth — extending
 * it would require a coordinated change across the router, query
 * analyzer, and persisted Zustand slice. Automations is reachable via:
 *
 *   - direct URL,
 *   - notifications drawer "vai all'automazione" link,
 *   - command-K palette (Phase 7),
 *   - the UserMenu shortcut once `<UserMenu>` ships it.
 *
 * If we ever decide automations DOES belong inside the 6-tab top bar,
 * extending ``WORKSPACE_TABS`` is the only edit point — every other module
 * picks it up automatically.
 */
export const Route = createFileRoute("/_authed/workspace/automations")({
  validateSearch: (raw: Record<string, unknown>) => AutomationsSearchSchema.parse(raw),
  // Warm the first automations page on hover/focus (matches the hub's mount
  // query: page 1 / pageSize 50) so the tab paints from cache.
  loader: ({ context: { queryClient } }) => {
    void queryClient.ensureQueryData(automationsListQueryOptions({ page: 1, pageSize: 50 }));
  },
  component: AutomationsHub,
});
